"use client";

import { useCallback, useEffect, useState } from "react";

export type WebSocketMessageBodyType = {
  ContentType: string;
  Id?: string;
};

export type WebSocketRequestMessageType = {
  Subscribe: WebSocketMessageBodyType;
};

export type WebSocketResponseMessageType = {
  Changed: WebSocketMessageBodyType[];
};

class WebSocketConnection {
  socket: WebSocket | null = null;
  keepAliveTimer = 0;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private isConnecting: boolean = false;
  private isClosing: boolean = false;
  private identifier: string = "";

  connect(
    url: string,
    onMessageCallback: (data: any) => void,
    onConnected?: (socket: WebSocket) => void,
    onErrorCallback?: (event: any) => void,
    shouldReconnect?: () => boolean,
    webSocketIdentifier?: string
  ) {
    console.debug(
      `Connecting to websocket: ${url} - identifier: ${webSocketIdentifier}`
    );
    this.identifier = webSocketIdentifier ?? "";
    const WSConnect = () => {
      this.isConnecting = true;
      try {
        const socket = new WebSocket(url);

        socket.addEventListener("open", () => {
          onConnected?.(socket);
          console.debug(`Websocket ${this.identifier} connected.`);
          if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
          }

          this.keepAliveTimer = window.setInterval(() => {
            socket.send(JSON.stringify({ "Keep-Alive": true }));
            console.debug(`Websocket ${this.identifier} kept alive.`);
          }, 30000);
          this.isConnecting = false;
          if (this.isClosing) {
            socket.close();
          }
        });

        socket.addEventListener("close", (event) => {
          console.debug(
            `Websocket ${this.identifier} closed. Reason: ${event.code}`
          );
          this.clearKeepAliveTimer();
          if (event.code !== 1000) {
            // let's try tor reconnect, because the socket has not been closed normally
            tryReconnect();
          }
        });

        socket.addEventListener("error", (event) => {
          console.error(
            `Websocket ${this.identifier} error: ${JSON.stringify(event)}`
          );
          onErrorCallback?.(event);
        });

        socket.addEventListener("message", (event) => {
          onMessageCallback(event.data);
        });

        return socket;
      } catch (error) {
        this.isConnecting = false;
        tryReconnect();
      }
    };

    const tryReconnect = () => {
      if (this.isClosing) return;
      this.reconnectTimer = setTimeout(() => {
        if (shouldReconnect?.() ?? false) {
          WSConnect();
        }
      }, 5000);
    };

    this.socket = WSConnect();
  }

  close() {
    console.debug(`Closing websocket ${this.identifier}`);
    this.clearReconnectTimer();
    if (this.isConnecting) {
      this.isClosing = true;
      return;
    }

    this.clearKeepAliveTimer();
    this.socket?.close();
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private clearKeepAliveTimer() {
    if (this.keepAliveTimer > 0) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = 0;
    }
  }
}

export type WebSocketCallback = (changes: WebSocketMessageBodyType[]) => void;

export function useWebSocketInternal(
  url: string,
  getRequest: () => WebSocketRequestMessageType,
  onMessageCallback: WebSocketCallback,
  onErrorCallback?: (event: any) => VoidFunction,
  webSocketIdentifier?: string
) {
  const [id, setId] = useState<number>(1);

  const getIdentifier = useCallback(
    () => `${webSocketIdentifier}-${id}`,
    [webSocketIdentifier, id]
  );

  function onMessageReceived(data: any) {
    const res: WebSocketResponseMessageType = JSON.parse(data);
    onMessageCallback(res.Changed);
  }

  function sendRequest(socket: WebSocket) {
    const data: WebSocketRequestMessageType = getRequest();
    socket.send(JSON.stringify(data));
  }

  useEffect(() => {
    const locationPathName = window.location.pathname;

    const wsc: WebSocketConnection = new WebSocketConnection();
    if (url) {
      wsc.connect(
        url,
        onMessageReceived,
        sendRequest,
        onErrorCallback,
        () => locationPathName == window.location.pathname,
        getIdentifier()
      );
    }

    setId((prevId) => prevId + 1);

    return () => {
      wsc.close();
    };
    // eslint-disable-next-line
  }, [url]);
}
